import { useState, useCallback, useEffect, useRef } from 'react';
import { Linkedin, ArrowLeft, ArrowRight, RefreshCw, ChevronDown, ChevronUp, Search, Loader2, ExternalLink, Play, Square, LogIn, KeyRound, Trash2, Smartphone } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useSession } from '@/hooks/useSession';
import { LinkedInAutomationTab } from '../components/LinkedInAutomationTab';

const API_BASE = '/linkedin-api/sessions';

interface ScreenshotData {
  screenshot: string;
  url: string;
  title: string;
}

async function apiCall<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers } as Record<string, string>,
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || res.statusText);
  }
  return res.json() as Promise<T>;
}

export function LinkedInPage() {
  const [tab, setTab] = useState('browse');
  const { user } = useSession();
  const userId = user?._id || 'default';

  const [screenshotData, setScreenshotData] = useState<ScreenshotData | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [navUrl, setNavUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [linkedinEmail, setLinkedinEmail] = useState('');
  const [linkedinPassword, setLinkedinPassword] = useState('');
  const [hasSavedCreds, setHasSavedCreds] = useState(false);
  const [savedUsername, setSavedUsername] = useState<string | null>(null);

  const isOnLoginPage = screenshotData?.url?.includes('linkedin.com/login') && !screenshotData?.url?.includes('checkpoint');
  const isOnCheckpoint = screenshotData?.url?.includes('linkedin.com/checkpoint') || screenshotData?.url?.includes('challengesV2');
  const checkpointPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-poll when on checkpoint page — check every 5s if user approved on phone
  useEffect(() => {
    if (isOnCheckpoint && isActive && !isLoading) {
      toast.info('Check your LinkedIn app', {
        description: 'Open the LinkedIn app on your phone and tap "Yes" to approve the sign-in.',
        icon: <Smartphone className="h-5 w-5" />,
        duration: 15000,
        id: 'linkedin-checkpoint',
      });

      checkpointPollRef.current = setInterval(async () => {
        try {
          const data = await apiCall<ScreenshotData>('/screenshot', { headers });
          if (!data.url.includes('checkpoint') && !data.url.includes('challengesV2')) {
            // User approved! Update state
            setScreenshotData(data);
            setNavUrl(data.url);
            toast.success('LinkedIn sign-in approved!', { id: 'linkedin-checkpoint' });
            if (checkpointPollRef.current) clearInterval(checkpointPollRef.current);
          }
        } catch {
          // ignore polling errors
        }
      }, 5000);

      return () => {
        if (checkpointPollRef.current) clearInterval(checkpointPollRef.current);
      };
    }
  }, [isOnCheckpoint, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if credentials are saved on mount
  useEffect(() => {
    apiCall<{ hasCreds: boolean; username: string | null }>('/credentials', { headers })
      .then((data) => {
        setHasSavedCreds(data.hasCreds);
        setSavedUsername(data.username);
      })
      .catch(() => {});
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = { 'x-user-id': userId };

  const doAction = useCallback(async (fn: () => Promise<ScreenshotData>) => {
    setIsLoading(true);
    try {
      const data = await fn();
      setScreenshotData(data);
      setNavUrl(data.url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Action failed', { description: message });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleStart = () => doAction(async () => {
    const data = await apiCall<ScreenshotData>('/start', { method: 'POST', headers, body: '{}' });
    setIsActive(true);
    return data;
  });

  const handleStop = async () => {
    setIsLoading(true);
    try {
      await apiCall('/stop', { method: 'POST', headers });
      setIsActive(false);
      setScreenshotData(null);
      setNavUrl('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to stop', { description: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavigate = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!navUrl.trim()) return;
    doAction(() => apiCall<ScreenshotData>('/navigate', {
      method: 'POST', headers, body: JSON.stringify({ url: navUrl.trim() }),
    }));
  };

  const handleAction = (action: string, params?: Record<string, string>) => {
    doAction(() => apiCall<ScreenshotData>('/action', {
      method: 'POST', headers, body: JSON.stringify({ action, params }),
    }));
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    handleAction('search', { query: searchQuery.trim() });
  };

  const handleLogin = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!linkedinEmail.trim() || !linkedinPassword.trim()) return;
    doAction(async () => {
      const data = await apiCall<ScreenshotData>('/action', {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'login', params: { username: linkedinEmail.trim(), password: linkedinPassword.trim() } }),
      });
      // If login succeeded (no longer on login page), update saved creds state
      if (!data.url.includes('linkedin.com/login')) {
        setHasSavedCreds(true);
        setSavedUsername(linkedinEmail.trim());
        setLinkedinPassword('');
        toast.success('Logged in and credentials saved');
      }
      return data;
    });
  };

  const handleDeleteCreds = async () => {
    try {
      await apiCall('/credentials', { method: 'DELETE', headers });
      setHasSavedCreds(false);
      setSavedUsername(null);
      toast.success('Saved credentials removed');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to remove credentials', { description: message });
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Linkedin className="h-6 w-6 text-[#0A66C2]" />
            <div>
              <h1 className="text-2xl font-semibold">LinkedIn</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {isActive ? screenshotData?.title || 'Connected' : 'Start a session to browse'}
                {hasSavedCreds && !isActive && <span className="ml-2 text-green-600">({savedUsername})</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isActive ? (
              <Button onClick={handleStart} disabled={isLoading}>
                {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                Start Session
              </Button>
            ) : (
              <>
                <a href={screenshotData?.url || 'https://www.linkedin.com'} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in browser
                  </Button>
                </a>
                <Button variant="outline" size="sm" onClick={handleStop} disabled={isLoading}>
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b bg-white px-6">
          <TabsList className="h-10">
            <TabsTrigger value="browse">Browse</TabsTrigger>
            <TabsTrigger value="automation">Automation</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="browse" className="flex-1 m-0 flex flex-col overflow-hidden">
          {!isActive ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center space-y-4">
                <Linkedin className="h-12 w-12 mx-auto opacity-30" />
                <p className="text-lg">Click "Start Session" to begin browsing LinkedIn</p>
                <p className="text-sm">Your login will persist between sessions</p>
              </div>
            </div>
          ) : (
            <>
              {/* Navigation bar */}
              <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleAction('go-back')} disabled={isLoading}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleAction('go-forward')} disabled={isLoading}>
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleAction('refresh')} disabled={isLoading}>
                  <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                </Button>
                <form onSubmit={handleNavigate} className="flex-1 flex gap-2">
                  <Input
                    value={navUrl}
                    onChange={(e) => setNavUrl(e.target.value)}
                    className="h-8 text-sm font-mono"
                    placeholder="URL..."
                  />
                  <Button type="submit" size="sm" variant="outline" className="h-8" disabled={isLoading}>
                    Go
                  </Button>
                </form>
              </div>

              {/* Screenshot area */}
              <div className="flex-1 overflow-auto relative">
                {isLoading && (
                  <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-[#0A66C2]" />
                  </div>
                )}

                {/* Checkpoint overlay — waiting for app approval */}
                {isOnCheckpoint && !isLoading && (
                  <div className="absolute inset-0 bg-black/40 z-10 flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-4 text-center">
                      <Smartphone className="h-10 w-10 mx-auto text-[#0A66C2]" />
                      <h3 className="text-lg font-semibold">Check your LinkedIn app</h3>
                      <p className="text-sm text-muted-foreground">
                        Open the LinkedIn app on your phone and tap <strong>Yes</strong> to approve the sign-in.
                      </p>
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Waiting for approval...
                      </div>
                    </div>
                  </div>
                )}

                {/* Login overlay when on LinkedIn login page */}
                {isOnLoginPage && !isLoading && (
                  <div className="absolute inset-0 bg-black/40 z-10 flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-4">
                      <div className="flex items-center gap-2">
                        <LogIn className="h-5 w-5 text-[#0A66C2]" />
                        <h3 className="text-lg font-semibold">Sign in to LinkedIn</h3>
                      </div>
                      {hasSavedCreds ? (
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            Saved credentials for <span className="font-medium">{savedUsername}</span>
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Auto-login failed. You may need to update your credentials.
                          </p>
                          <Button variant="outline" size="sm" onClick={handleDeleteCreds} className="w-full">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove saved credentials
                          </Button>
                        </div>
                      ) : null}
                      <form onSubmit={handleLogin} className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor="li-email">Email</Label>
                          <Input
                            id="li-email"
                            type="email"
                            placeholder="email@example.com"
                            value={linkedinEmail}
                            onChange={(e) => setLinkedinEmail(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="li-password">Password</Label>
                          <Input
                            id="li-password"
                            type="password"
                            placeholder="Password"
                            value={linkedinPassword}
                            onChange={(e) => setLinkedinPassword(e.target.value)}
                          />
                        </div>
                        <Button type="submit" className="w-full" disabled={isLoading || !linkedinEmail.trim() || !linkedinPassword.trim()}>
                          <KeyRound className="h-4 w-4 mr-2" />
                          Sign in & Save
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          Credentials are saved locally and used to auto-login next time.
                        </p>
                      </form>
                    </div>
                  </div>
                )}

                {screenshotData?.screenshot && (
                  <img
                    src={`data:image/jpeg;base64,${screenshotData.screenshot}`}
                    alt="LinkedIn"
                    className="w-full"
                  />
                )}
              </div>

              {/* Action bar */}
              <div className="flex items-center gap-2 px-4 py-2 border-t bg-gray-50">
                <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search LinkedIn..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 text-sm pl-8"
                    />
                  </div>
                  <Button type="submit" size="sm" variant="outline" className="h-8" disabled={isLoading || !searchQuery.trim()}>
                    Search
                  </Button>
                </form>
                <div className="flex items-center gap-1 ml-auto">
                  <Button variant="ghost" size="sm" className="h-8" onClick={() => handleAction('scroll-up')} disabled={isLoading}>
                    <ChevronUp className="h-4 w-4 mr-1" /> Up
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8" onClick={() => handleAction('scroll-down')} disabled={isLoading}>
                    <ChevronDown className="h-4 w-4 mr-1" /> Down
                  </Button>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="automation" className="flex-1 m-0">
          <LinkedInAutomationTab userId={userId} isActive={isActive} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
